import React, { useEffect, useState } from 'react'
import Delete from '../../../../assets/svg/delete.svg'
import { useParams, useHistory } from 'react-router-dom'
import { Initials, formatNewDateInWords } from '../../../../utils/functions'
import LoadingBar from '../../../../components/loader-bar'
import Button from '../../../../components/button'
import { setAlert } from '../../../../redux/components/components-slice'
import { store } from '../../../../redux'
import {
  useDeleteGoal,
  useDeleteSubGoal,
  useGetSingleGoal,
  useReviewSubGoal,
} from '../../../../redux/human-resources/hook/performance'
import DashboardLayout from '../../../../components/layouts/dashboard'
import UnauthorizedPage from '../../../errors/unauthorized'
import { useSelector } from 'react-redux'
import DoYouWantToModal from '../../../../components/modal/do-you-want-to'
import AddGoal from './AddGoal'

const CompanyGoal = () => {
  const { user } = useSelector((state) => state.userSlice)
  const [unauthorized, setUnauthorized] = useState(false)

  useEffect(() => {
    if (user) {
      if (user.permissions.company_permission.role !== 'Owner') {
        if (user.permissions.company_permission.performance_read !== 1) {
          setUnauthorized(true)
        }
      }
    }
  }, [user])

  const history = useHistory()

  const { id } = useParams()

  const [showDoYouWant, setShowDoYouWant] = useState(false)
  const [goals, setGoals] = useState(false)
  const [deleteContext, setDeleteContext] = useState({
    id: null,
    type: null,
  })

  const { mutateAsync: deleteSubGoal, isLoading: deleteSubGoalLoading } =
    useDeleteSubGoal()
  const { mutateAsync: deleteGoal, isLoading: deleteGoalLoading } =
    useDeleteGoal()

  const { mutateAsync: reviewSubGoal, isLoading: reviewSubGoalLoading } =
    useReviewSubGoal()
  const { data: singleGoal, isLoading, refetch } = useGetSingleGoal(id)

  const handleDeleteGoal = async (goalId) => {
    let payload = { goal_ids: [goalId] }
    await deleteGoal(payload).then(() => {
      history.push('/performance')
    })
  }

  const handleDeleteSubGoal = async (subGoalId) => {
    try {
      await deleteSubGoal({ sub_goal_ids: [subGoalId] })
    } catch (error) {
      console.error('Failed to delete sub-goal:', error)
    } finally {
      refetch()
    }
  }

  const confirmDeleteGoal = (goalId) => {
    setDeleteContext({ id: goalId, type: 'goal' })
    setShowDoYouWant(true)
  }

  const confirmDeleteSubGoal = (subGoalId) => {
    setDeleteContext({ id: subGoalId, type: 'subgoal' })
    setShowDoYouWant(true)
  }

  const calculateGoalProgress = (subGoals) => {
    if (!subGoals || subGoals?.length === 0) return 0
    const completed = subGoals?.filter((goal) => goal?.status === '1').length
    return Math.round((completed / subGoals?.length) * 100)
  }

  const progress = calculateGoalProgress(singleGoal?.sub_goals || [])
  // const isOwner = singleGoal?.owners?.[0]?.id

  return (
    <DashboardLayout title='Performance Management'>
      {goals ? (
        <AddGoal
          isOpen={goals}
          closeModal={() => setGoals(false)}
          type={0}
          existingGoal={singleGoal}
        />
      ) : null}
      {unauthorized ? (
        <UnauthorizedPage />
      ) : (
        <>
          {showDoYouWant && (
            <DoYouWantToModal
              isOpen={showDoYouWant}
              closeModal={() => setShowDoYouWant(false)}
              id={deleteContext.id}
              type='delete'
              title='Are you sure you want to delete?'
              subtitle={
                deleteContext.type === 'goal'
                  ? 'This will remove the entire goal and its sub-goals.'
                  : 'This will remove only this sub-goal.'
              }
              buttonText='DELETE'
              loading={
                deleteContext.type === 'goal'
                  ? deleteGoalLoading
                  : deleteSubGoalLoading
              }
              btnFunction={async (id) => {
                if (deleteContext.type === 'goal') {
                  await handleDeleteGoal(id)
                } else {
                  await handleDeleteSubGoal(id)
                }
                setShowDoYouWant(false)
              }}
            />
          )}

          {isLoading ? (
            <div className=' w-full flex items-center justify-center h-[80px]'>
              <LoadingBar loading={isLoading} />
            </div>
          ) : null}
          {isLoading ? (
            <>
              <div
                className={
                  'flex flex-row w-[100%] items-center justify-center h-[200px] animate-pulse p1-bold text-hr-primary-1'
                }
              >
                Loading...
              </div>
            </>
          ) : (
            <div className='w-full'>
              <div className='flex flex-col lg:flex-row gap-4'>
                <aside className='lg:w-[25%] w-full  border p-4'>
                  <div className='flex flex-col gap-4'>
                    <div>
                      <h2 className='text-sm font-semibold text-[#78787D]'>
                        Goal Owner
                      </h2>
                      <div className='flex items-center space-x-2 mt-1'>
                        <div className='w-6 h-6 rounded-full border-2 border-white text-xs flex items-center justify-center text-white bg-hr-primary-1'>
                          {Initials(singleGoal?.owners[0]?.first_name)}
                        </div>
                        <span className='text-sm font-medium'>
                          {singleGoal?.owners[0]?.first_name}
                        </span>
                      </div>
                    </div>

                    <div>
                      <h2 className='text-sm font-semibold text-[#78787D]'>
                        Members
                      </h2>
                      <div className='flex flex-col space-y-1 mt-1'>
                        {singleGoal?.members?.map((member) => (
                          <div
                            key={member?.id}
                            className='flex items-center justify-between text-sm'
                          >
                            <div className='flex items-center gap-2.5'>
                              <div className='w-6 h-6 rounded-full border-2 border-white text-xs flex items-center justify-center text-white bg-hr-primary-1'>
                                {Initials(member?.first_name)}
                              </div>
                              <span>{member?.first_name}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className='flex flex-col gap-2.5'>
                      <h2 className='text-sm font-semibold text-[#78787D]'>
                        Target Date
                      </h2>
                      <p className='text-sm font-medium text-black'>
                        {formatNewDateInWords(singleGoal?.end_date)}
                      </p>{' '}
                    </div>
                      <div className='w-[160px] h-10'>
                        <Button
                          text={`Edit Goal`}
                          className='flex gap-2 h-[35px] w-full px-3 py-2'
                          textClass='!text-[12px]'
                          onClick={() => {
                            setGoals(true)
                          }}
                        />
                      </div>
                  </div>
                </aside>

                <main className='flex flex-col gap-5 w-full lg:w-[75%]'>
                  <div className='flex flex-col gap-2 w-full lg:w-[80%] p-4 border'>
                    <div className='flex items-start md:items-center justify-between gap-2'>
                      <h1 className='text-base font-semibold'>
                        {singleGoal?.name}
                      </h1>
                        <button
                          onClick={() => confirmDeleteGoal(singleGoal.id)}
                          disabled={deleteGoalLoading}
                        >
                          <img src={Delete} alt='Delete Goal' />
                        </button>
                    </div>
                    <div className='flex flex-col gap-2.5 items-start'>
                      <p className='text-sm font-medium'>
                        {progress}% completed
                      </p>{' '}
                      <div className='w-full bg-[#DDEBE1] h-1'>
                        <div
                          className='h-1 bg-[#238B45]'
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className='flex flex-col items-start gap-2'>
                      <div className='flex flex-col gap-1'>
                        <h1 className='text-[#78787D] text-sm'>
                          Goal Description
                        </h1>
                        <p className='text-black text-sm '>
                          {singleGoal?.description}
                        </p>
                      </div>
                      <div className='flex flex-col gap-1'>
                        <h2 className='text-sm font-semibold text-[#78787D]'>
                          Target Date
                        </h2>
                        <p className='text-sm font-medium text-black'>
                          {formatNewDateInWords(singleGoal?.end_date)}
                        </p>{' '}
                      </div>
                      {singleGoal?.extension_date?.length > 0 && (
                        <div className='flex flex-col gap-1'>
                          <p className='text-sm font-medium text-[#78787D]'>
                            Previous End Date
                          </p>
                          {singleGoal.extension_date.map((g, index) => (
                            <div className='flex flex-col gap-1' key={g.id}>
                              <p className='text-[#000005] font-medium text-sm '>
                                Extension {index + 1}:{' '}
                                {formatNewDateInWords(g.previous_end_date)}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className='flex flex-col gap-2'>
                      <div className='flex flex-col items-start md:items-center gap-4 md:flex-row'>
                        <div className='flex flex-col gap-2.5'>
                          <h2 className='font-semibold text-[18px]'>
                            Sub Goals
                          </h2>
                          <h1 className='text-sm font-medium text-[#78787D] mb-2 max-w-[600px]'>
                            Once you mark a sub goal as completed, It updates
                            the goal progress bar. Reviewer can rework the goal
                            if the goal has not been completed which resets the
                            progress bar
                          </h1>
                        </div>
                      </div>
                      {singleGoal?.sub_goals?.map((subGoal, index) => (
                        <section
                          key={subGoal?.id}
                          className='flex flex-col gap-2'
                        >
                          <div className='flex justify-between items-center'>
                            <h2 className='font-medium text-sm'>
                              Sub-Goal {index + 1}
                            </h2>
                              <button
                                onClick={() => confirmDeleteSubGoal(subGoal.id)}
                                disabled={deleteSubGoalLoading}
                              >
                                <img src={Delete} alt='Delete Goal' />
                              </button>
                          </div>

                          <div className='flex flex-col gap-1'>
                            <h1 className='text-[#78787D] text-sm'>
                              Sub Goal name
                            </h1>
                            <p className='text-black text-sm '>
                              {subGoal?.name}
                            </p>
                          </div>
                          <div className='flex flex-col gap-1 mb-2'>
                            <h1 className='text-[#78787D] text-sm'>
                              Assignees
                            </h1>
                            {subGoal.assignees.map((a, index) => (
                              <p className='text-black text-sm' key={index}>
                                {a.first_name} {a.last_name}
                              </p>
                            ))}
                          </div>

                          <div className='flex flex-col gap-1'>
                            <h2 className='text-sm font-medium text-[#78787D]'>
                              Target Date
                            </h2>
                            <p className='text-sm font-medium text-black'>
                              {formatNewDateInWords(subGoal?.target_date)}
                            </p>{' '}
                          </div>

                          {subGoal.status !== '1' && (
                            <>
                              <div className='w-[280px]'>
                                {subGoal.assignees?.some(
                                  (a) => a.id === user.employee_id
                                ) && (
                                  <FormSwitch
                                    label='Mark sub-goal as complete'
                                    name={`switch-complete-${subGoal.id}`}
                                    className='!text-sm'
                                    checked={subGoal.status !== '0'}
                                    onClick={() => handleToggleChange(subGoal)}
                                  />
                                )}
                              </div>
                            </>
                          )}

                          {subGoal?.extension_date?.length > 0 && (
                            <div className='flex flex-col gap-1'>
                              <p className='text-sm font-medium text-[#78787D]'>
                                Previous Target Date
                              </p>
                              {subGoal.extension_date.map((g, index) => (
                                <div className='flex flex-col gap-1' key={g.id}>
                                  <p className='text-[#000005] font-medium text-sm '>
                                    Extension {index + 1}:{' '}
                                    {formatNewDateInWords(
                                      g.previous_target_date
                                    )}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}

                          <div className='flex flex-col gap-2 mb-3'>
                            {subGoal?.status === '1' ? (
                              <p className='text-xs mt-2 font-medium cursor-pointer text-[#238B45]'>
                                Completed by Assignees
                              </p>
                            ) : (
                              <p className='text-xs mt-2 font-medium cursor-pointer text-red-500'>
                                Not Completed by Assignees
                              </p>
                            )}

                            {subGoal?.status === '1' && (
                              <div className='flex items-center gap-2.5'>
                                <div className='w-[200px] h-10'>
                                  <Button
                                    text={`Rework Sub Goal ${index + 1}`}
                                    type='submit'
                                    theme='secondary'
                                    className='flex gap-2 h-[35px] w-[150px] px-3 py-2'
                                    loading={reviewSubGoalLoading}
                                    textClass={'!text-[12px]'}
                                    onClick={async () => {
                                      if (subGoal?.status !== '1') {
                                        store.dispatch(
                                          setAlert(
                                            true,
                                            'warning',
                                            'Sub goal has not been completed by assignee'
                                          )
                                        )
                                        return
                                      }
                                      await reviewSubGoal({
                                        marks: [{ id: subGoal.id, action: 0 }],
                                      })
                                      refetch()
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </section>
                      ))}
                    </div>
                  </div>
                </main>
              </div>
            </div>
          )}
        </>
      )}
    </DashboardLayout>
  )
}

export default CompanyGoal
